/*
 * variant-choice-gate.js  (v4)
 * Forces an explicit choice on required variant options before add to cart.
 *
 * v4 changes:
 *  - GATE_PATTERN now also covers clasp / buckle options. Matching is by
 *    keyword, so a future rename to plain 'Clasp Type' is still caught.
 *
 * v3 changes:
 *  - Fixes iOS Safari text inflation: locking body scroll made Safari re-run
 *    its automatic text-size-adjust pass and enlarge text behind the modal.
 *    Styles are now injected on load (not on open) and opt out explicitly.
 *  - Scroll lock preserves scroll position instead of using body overflow.
 *  - Debug marker off.
 */
(function () {
  'use strict';

  var GATE_PATTERN = /(wrist\s*size|length|lug\s*width|clasp|buckle)/i;
  var DEBUG_MARKER = false;

  var touched = Object.create(null);
  var bypass = false;
  var deepLinked = false;
  var modalEl = null;
  var lockedScrollY = 0;

  /* ---------------------------------------------------------------- utils */

  function clean(str) {
    return String(str || '').replace(/\s+/g, ' ').trim();
  }

  function optionNameFromInput(input) {
    return clean((input.getAttribute('name') || '').replace(/-\d+$/, ''));
  }

  function getForm() {
    return document.querySelector('form[data-type="add-to-cart-form"]');
  }

  function getVariantId(form) {
    var input = form && form.querySelector('[name="id"]');
    return input ? input.value : null;
  }

  function headingFor(input) {
    var details = input.closest('details');
    var title = details && details.querySelector('summary .accordion__title');
    if (title) return clean(title.textContent);
    var fieldset = input.closest('fieldset');
    var legend = fieldset && fieldset.querySelector('legend');
    return legend ? clean(legend.textContent) : '';
  }

  function labelTextFor(input) {
    var label = input.id
      ? document.querySelector('label[for="' + String(input.id).replace(/["\\]/g, '\\$&') + '"]')
      : null;
    if (!label) return clean(input.value);
    var clone = label.cloneNode(true);
    var hidden = clone.querySelectorAll('.visually-hidden');
    for (var i = 0; i < hidden.length; i++) hidden[i].parentNode.removeChild(hidden[i]);
    return clean(clone.textContent) || clean(input.value);
  }

  function waitFor(test, timeoutMs, done) {
    var started = Date.now();
    (function poll() {
      if (test() || Date.now() - started > timeoutMs) return done();
      setTimeout(poll, 80);
    })();
  }

  /* ------------------------------------------------------- option scanning */

  function collectGroups() {
    var groups = [];
    var byName = Object.create(null);
    var inputs = document.querySelectorAll('variant-selects input[type="radio"]');

    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var name = optionNameFromInput(input);
      if (!name) continue;
      if (!byName[name]) {
        byName[name] = { name: name, label: headingFor(input) || name, inputs: [] };
        groups.push(byName[name]);
      }
      byName[name].inputs.push(input);
    }
    return groups;
  }

  function pendingGroups() {
    if (deepLinked) return [];
    var pending = [];
    var groups = collectGroups();
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      if (g.inputs.length < 2) continue;
      if (!GATE_PATTERN.test(g.name)) continue;
      if (touched[g.name]) continue;
      pending.push(g);
    }
    return pending;
  }

  /* --------------------------------------------------------------- styles */

  function injectStyles() {
    if (document.getElementById('vcg-styles')) return;
    var css = [
      /* Stop iOS Safari inflating text when the modal locks scrolling. */
      'html{-webkit-text-size-adjust:100%;text-size-adjust:100%;}',
      'body{-webkit-text-size-adjust:100%;text-size-adjust:100%;}',
      'html.vcg-locked{position:fixed;width:100%;overflow:hidden;}',
      '.vcg-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:2147483000;',
      'display:flex;align-items:flex-end;justify-content:center;}',
      '@media(min-width:750px){.vcg-backdrop{align-items:center;}}',
      '.vcg-sheet{background:#fff;width:100%;max-width:520px;max-height:85vh;overflow-y:auto;',
      'padding:2.4rem 2rem 2rem;border-radius:12px 12px 0 0;}',
      '@media(min-width:750px){.vcg-sheet{border-radius:12px;}}',
      '.vcg-head{display:flex;justify-content:space-between;align-items:flex-start;gap:1rem;margin-bottom:.6rem;}',
      '.vcg-title{font-size:1.7rem;letter-spacing:.05rem;margin:0;text-transform:uppercase;}',
      '.vcg-sub{font-size:1.3rem;color:#6b6b6b;margin:0 0 1.6rem;}',
      '.vcg-close{background:none;border:0;font-size:2.4rem;line-height:1;cursor:pointer;color:#3e3e3e;padding:0 .4rem;}',
      '.vcg-group{margin-bottom:2rem;}',
      '.vcg-group-label{font-size:1.3rem;letter-spacing:.08rem;text-transform:uppercase;',
      'color:#3e3e3e;margin:0 0 .9rem;font-weight:600;}',
      '.vcg-opts{display:flex;flex-wrap:wrap;gap:.8rem;}',
      '.vcg-opt{border:1px solid #3e3e3e;background:transparent;color:#4f4944;border-radius:0;',
      'padding:1.1rem 1.8rem;font-size:1.4rem;letter-spacing:.05rem;cursor:pointer;line-height:1;}',
      '.vcg-opt[aria-pressed="true"]{background:#3e3e3e;color:#fff;}',
      '.vcg-opt[disabled]{opacity:.45;text-decoration:line-through;cursor:not-allowed;}',
      '.vcg-confirm{width:100%;padding:1.5rem;background:#3e3e3e;color:#fff;border:0;',
      'font-size:1.5rem;letter-spacing:.1rem;text-transform:uppercase;cursor:pointer;}',
      '.vcg-confirm[disabled]{opacity:.4;cursor:not-allowed;}',
      '.vcg-marker{font-size:10px;letter-spacing:.05rem;color:#8a8a8a;margin:6px 0 0;text-align:center;}'
    ].join('');
    var style = document.createElement('style');
    style.id = 'vcg-styles';
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
  }

  /* Inject immediately so text-size-adjust is set before any modal opens. */
  injectStyles();

  /* ----------------------------------------------------------- scroll lock */

  function lockScroll() {
    lockedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.documentElement.style.top = -lockedScrollY + 'px';
    document.documentElement.classList.add('vcg-locked');
  }

  function unlockScroll() {
    if (!document.documentElement.classList.contains('vcg-locked')) return;
    document.documentElement.classList.remove('vcg-locked');
    document.documentElement.style.top = '';
    window.scrollTo(0, lockedScrollY);
  }

  /* ---------------------------------------------------------------- modal */

  function closeModal() {
    if (!modalEl) return;
    if (modalEl.parentNode) modalEl.parentNode.removeChild(modalEl);
    modalEl = null;
    unlockScroll();
  }

  function openModal(groups, form) {
    injectStyles();
    closeModal();

    var picks = Object.create(null);

    var backdrop = document.createElement('div');
    backdrop.className = 'vcg-backdrop';
    backdrop.setAttribute('role', 'dialog');
    backdrop.setAttribute('aria-modal', 'true');

    var sheet = document.createElement('div');
    sheet.className = 'vcg-sheet';

    var head = document.createElement('div');
    head.className = 'vcg-head';

    var title = document.createElement('h2');
    title.className = 'vcg-title';
    title.textContent = groups.length > 1 ? 'A couple of choices first' : 'One choice first';

    var close = document.createElement('button');
    close.className = 'vcg-close';
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '&times;';
    close.addEventListener('click', closeModal);

    head.appendChild(title);
    head.appendChild(close);
    sheet.appendChild(head);

    var sub = document.createElement('p');
    sub.className = 'vcg-sub';
    sub.textContent = 'This affects the fit, so we would rather you picked than we guessed.';
    sheet.appendChild(sub);

    var confirm = document.createElement('button');
    confirm.className = 'vcg-confirm';
    confirm.type = 'button';
    confirm.disabled = true;
    confirm.textContent = 'Confirm and add to cart';

    function refreshConfirm() {
      var ready = true;
      for (var i = 0; i < groups.length; i++) {
        if (!picks[groups[i].name]) ready = false;
      }
      confirm.disabled = !ready;
    }

    groups.forEach(function (group) {
      var wrap = document.createElement('div');
      wrap.className = 'vcg-group';

      var label = document.createElement('p');
      label.className = 'vcg-group-label';
      label.textContent = group.label;
      wrap.appendChild(label);

      var opts = document.createElement('div');
      opts.className = 'vcg-opts';

      group.inputs.forEach(function (input) {
        var btn = document.createElement('button');
        btn.className = 'vcg-opt';
        btn.type = 'button';
        btn.textContent = labelTextFor(input);
        btn.setAttribute('aria-pressed', 'false');
        if (input.classList.contains('disabled') || input.disabled) {
          btn.disabled = true;
          return opts.appendChild(btn);
        }
        btn.addEventListener('click', function () {
          var siblings = opts.querySelectorAll('.vcg-opt');
          for (var i = 0; i < siblings.length; i++) siblings[i].setAttribute('aria-pressed', 'false');
          btn.setAttribute('aria-pressed', 'true');
          picks[group.name] = input.id;
          refreshConfirm();
        });
        opts.appendChild(btn);
      });

      wrap.appendChild(opts);
      sheet.appendChild(wrap);
    });

    confirm.addEventListener('click', function () {
      confirm.disabled = true;
      confirm.textContent = 'Adding\u2026';
      applyAndSubmit(picks, form || getForm());
    });

    sheet.appendChild(confirm);
    backdrop.appendChild(sheet);
    backdrop.addEventListener('click', function (e) {
      if (e.target === backdrop) closeModal();
    });

    document.body.appendChild(backdrop);
    lockScroll();
    modalEl = backdrop;
  }

  /* ------------------------------------------------------ apply + submit */

  function applyAndSubmit(picks, form) {
    var changed = false;
    var last = null;

    Object.keys(picks).forEach(function (optionName) {
      touched[optionName] = true;
      var input = document.getElementById(picks[optionName]);
      if (!input || input.checked) return;
      input.checked = true;
      changed = true;
      last = input;
    });

    if (!changed || !last) {
      closeModal();
      submitNow();
      return;
    }

    var before = getVariantId(form);
    last.dispatchEvent(new Event('change', { bubbles: true }));

    waitFor(
      function () {
        var current = getForm();
        return !!current && getVariantId(current) !== before;
      },
      3000,
      function () {
        closeModal();
        submitNow();
      }
    );
  }

  function submitNow() {
    var form = getForm();
    if (!form) return;
    bypass = true;

    var btn = form.querySelector('[name="add"], [type="submit"]');
    if (btn) {
      btn.click();
    } else if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    setTimeout(function () {
      bypass = false;
    }, 1500);
  }

  /* ------------------------------------------------------------ listeners */

  function isAddToCartTrigger(el) {
    if (!el || !el.closest) return null;
    var btn = el.closest('[name="add"], .product-form__submit');
    if (!btn) return null;
    return btn.closest('form[data-type="add-to-cart-form"]') || getForm();
  }

  function maybeGate(e, form) {
    if (bypass) return false;
    var pending = pendingGroups();
    if (!pending.length) return false;

    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    openModal(pending, form);
    return true;
  }

  document.addEventListener(
    'change',
    function (e) {
      var target = e.target;
      if (!target || target.type !== 'radio' || !e.isTrusted) return;
      if (!target.closest || !target.closest('variant-selects')) return;
      touched[optionNameFromInput(target)] = true;
    },
    true
  );

  document.addEventListener(
    'click',
    function (e) {
      if (!e.isTrusted) return;
      var form = isAddToCartTrigger(e.target);
      if (!form) return;
      maybeGate(e, form);
    },
    true
  );

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
      maybeGate(e, form);
    },
    true
  );

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeModal();
  });

  try {
    deepLinked = new URLSearchParams(window.location.search).has('variant');
  } catch (err) {
    deepLinked = window.location.search.indexOf('variant=') !== -1;
  }

  window.__vcg = {
    groups: collectGroups,
    pending: pendingGroups,
    touched: touched
  };

  if (DEBUG_MARKER) {
    document.addEventListener('DOMContentLoaded', function () {
      var form = getForm();
      if (!form) return;
      var groups = collectGroups();
      var gated = [];
      for (var i = 0; i < groups.length; i++) {
        if (GATE_PATTERN.test(groups[i].name) && groups[i].inputs.length > 1) {
          gated.push(groups[i].name);
        }
      }
      var note = document.createElement('p');
      note.className = 'vcg-marker';
      note.textContent = 'size check active \u2014 gating: ' + (gated.join(', ') || 'nothing found');
      form.appendChild(note);
    });
  }
})();
